import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { CommunicationPageDto } from '@his/shared';
import { get, qs } from '@/lib/api';
import { visibleInterval } from '@/lib/polling';
import { cn, dateTime, humanize, relative } from '@/lib/utils';
import { tr } from '@/lib/i18n-core';
import { DataTable, FilterField, SearchFilterBar } from '@/components/ui/layout';
import { Badge, Button, Select, StatusBadge } from '@/components/ui/primitives';
import { ExchangeBadge } from './insurance-status';
import { MessagesButton } from './flow/MessagesDrawer';
import { insuranceTabPath, withReturnTo } from './insurance-routes';

const PRIORITY_VARIANT: Record<string, 'destructive' | 'warning' | 'secondary'> = { stat: 'destructive', asap: 'destructive', urgent: 'warning', routine: 'secondary' };

export function CommunicationsTab() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('open');
  const [direction, setDirection] = useState<'inbound' | 'outbound'>('inbound');
  const byDue = direction === 'inbound' && status === 'open';

  const list = useInfiniteQuery({
    queryKey: ['nphies-communications', status, direction],
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      get<CommunicationPageDto>(`/integrations/nphies/communications${qs({ status: status || undefined, direction, sort: byDue ? 'due' : 'recent', limit: 30, cursor: pageParam || undefined })}`),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: visibleInterval(60_000),
  });
  const rows = list.data?.pages.flatMap((page) => page.items);

  return (
    <>
      <p className="mb-2 text-xs text-muted-foreground">These are questions the insurer sent about a claim or a pre-authorisation. Answer them before they are due.</p>
      <SearchFilterBar
        filters={
          <>
            <FilterField label={tr('billing', 'direction')}>
              <Select className="w-56" value={direction} onChange={(event) => setDirection(event.target.value as 'inbound' | 'outbound')}>
                <option value="inbound">Requests from the insurer</option>
                <option value="outbound">Messages we sent</option>
              </Select>
            </FilterField>
            <FilterField label={tr('billing', 'status')}>
              <Select className="w-48" value={status} onChange={(event) => setStatus(event.target.value)}>
                <option value="open">{direction === 'outbound' ? 'Not sent yet' : 'To answer'}</option>
                <option value="replied">{direction === 'outbound' ? 'Sent' : 'Answered'}</option>
                <option value="closed">Closed</option>
                <option value="expired">Expired</option>
                <option value="">Every request</option>
              </Select>
            </FilterField>
          </>
        }
        onReset={
          direction !== 'inbound' || status !== 'open'
            ? () => {
                setDirection('inbound');
                setStatus('open');
              }
            : undefined
        }
      />
      <DataTable
        rows={rows}
        loading={list.isLoading} refreshing={list.isFetching}
        partial={Boolean(list.hasNextPage)}
        error={list.error}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(withReturnTo(`/insurance/requests/${row.id}/reply`, insuranceTabPath('requests')))}
        empty="No insurer requests here."
        footer={
          list.hasNextPage ? (
            <Button variant="outline" size="sm" className="mx-auto" onClick={() => void list.fetchNextPage()} loading={list.isFetchingNextPage}>
              Show more
            </Button>
          ) : undefined
        }
        columns={[
          { header: 'Received', sort: (row) => row.receivedAt, cell: (row) => <span className="whitespace-nowrap text-xs">{dateTime(row.receivedAt)}</span> },
          { header: 'About', cell: (row) => (row.claimNumber ? `Claim ${row.claimNumber}` : row.preAuthProcedure ? `Pre-authorisation: ${row.preAuthProcedure}` : 'Not matched to a record') },
          { header: 'Asks', cell: (row) => <span className="line-clamp-2 max-w-md text-xs">{row.subject ? `${row.subject}: ` : ''}{row.requestText ?? ''}</span> },
          { header: 'Priority', sort: (row) => row.priority, cell: (row) => <Badge variant={PRIORITY_VARIANT[row.priority] ?? 'secondary'}>{humanize(row.priority, 'priority')}</Badge> },
          {
            header: 'Due',
            sort: (row) => row.dueAt,
            cell: (row) => (row.dueAt ? <span className={cn('text-xs', row.overdue && 'font-semibold text-destructive')}>{row.overdue ? 'Past due ' : ''}{relative(row.dueAt)}</span> : '—'),
          },
          { header: 'Status', sort: (row) => row.status, cell: (row) => <span className="flex items-center gap-1"><StatusBadge status={row.status} tone={row.status === 'open' ? 'warning' : undefined} />{row.replyState && row.status !== 'open' && <ExchangeBadge state={row.replyState} />}</span> },
          { header: '', align: 'right', cell: (row) => <MessagesButton entityType="communication" entityId={row.id} title={tr('billing', 'nx_messages_for_communication')} /> },
        ]}
      />
    </>
  );
}
